import React, { useState, FunctionComponent } from 'react'
import { Routes } from '../../../../../../../constants/Routes'
import { ICompany, PurchaseOrderReceiptNotFound } from '@getgreenline/homi-shared'
import { useClipboard } from 'use-clipboard-copy'
import { message, Tooltip } from 'antd'
import { Collapse } from 'reactstrap'
import { Track } from '../../../../../../../Track'
import { BLAZE_COMPANY } from '../../../../../../../constants/general'

interface Props {
  company: ICompany
  foundInCatalogue: PurchaseOrderReceiptNotFound[]
  notFound: PurchaseOrderReceiptNotFound[]
  refreshReceiptProducts: () => void
  isRefreshing: boolean
}

export const POCatalogueProducts: FunctionComponent<Props> = (props: Props) => {
  const { company, foundInCatalogue, notFound, refreshReceiptProducts, isRefreshing } = props
  const [isExpanded, toggleExpanded] = useState(false)
  const totalNotFound = foundInCatalogue.length + notFound.length

  return (
    <section className='product-list-wrapper'>
      <header className='alert alert-warning mb-0'>
        <div
          className='d-flex justify-content-between align-items-center cursor-pointer'
          onClick={() => {
            toggleExpanded(!isExpanded)
            Track.track('dashboard:purchaseOrder_importProductsNotFoundClicked')
          }}
        >
          <span>{`${totalNotFound} products not found`}</span>

          <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`} aria-hidden='true' />
        </div>

        <Collapse isOpen={isExpanded}>
          <div className='mt-3'>
            <span>
              Please create the following products{' '}
              <a href={Routes.CREATE_PRODUCT(company.id)} target='_blank' rel='noreferrer'>
                here
              </a>
              , then press
            </span>

            <button
              className='button-link cursor-pointer ml-1'
              type='button'
              onClick={refreshReceiptProducts}
              disabled={isRefreshing}
            >
              refresh
            </button>

            {isRefreshing && (
              <i
                className={`fa fa-refresh ${isRefreshing ? 'fa-spin' : ''} ml-2`}
                aria-hidden='true'
              />
            )}
          </div>
        </Collapse>
      </header>

      <Collapse isOpen={isExpanded}>
        <ul className='rounded mt-3 product-list product-list__not-found'>
          {foundInCatalogue.map((catalogueProduct, index) => {
            const { sku, name } = catalogueProduct

            return (
              <MissingPOProduct
                key={`${index}_${sku}`}
                name={name}
                sku={sku}
                availableForImport={true}
                company={company}
              />
            )
          })}
          {notFound.map((catalogueProduct) => {
            const { sku, name } = catalogueProduct

            return (
              <MissingPOProduct
                key={sku}
                name={name}
                sku={sku}
                availableForImport={false}
                company={company}
              />
            )
          })}
        </ul>
      </Collapse>
    </section>
  )
}

interface IMissingPOSProductsProps extends PurchaseOrderReceiptNotFound {
  company: ICompany
  availableForImport: boolean
}

const MissingPOProduct = ({ name, sku, company, availableForImport }: IMissingPOSProductsProps) => {
  const clipboard = useClipboard()

  const copyToClipboard = (sku: string) => {
    Track.track('dashboard:purchaseOrder_importSKUCopied')

    clipboard.copy(sku)
    message.success(`${sku} copied`)
  }

  return (
    <li className='row ml-0 mr-0 mb-3'>
      <Tooltip
        title={
          <div>
            <p>Click to copy the SKU</p>
            <div className='mt-2'>
              {availableForImport ? (
                <>
                  This product is available to import from the province product catalogue. Import to
                  an{' '}
                  <a href={Routes.DASHBOARD_PRODUCTS(company.id)} target='_blank' rel='noreferrer'>
                    existing product
                  </a>{' '}
                  or create a{' '}
                  <a href={Routes.CREATE_PRODUCT(company.id)} target='_blank' rel='noreferrer'>
                    new product
                  </a>
                </>
              ) : (
                <>
                  If this product is imported from a provincial order, please send the latest list
                  to <a href='mailto:support-ca@blaze.me'>{BLAZE_COMPANY}</a>
                </>
              )}
            </div>
          </div>
        }
      >
        <main
          className='col-12 p-0 product-list__main cursor-pointer'
          onClick={() => copyToClipboard(sku)}
        >
          <h2 className='product-name'>
            <span>{name}</span>
          </h2>

          <small className='product-list__description'>
            <span>
              <i className='fa fa-files-o product-list__copy-icon mr-1' aria-hidden='true' />
              {`${sku || 'Empty SKU'} ${company.province === 'AB' ? `| category` : ''}`}
            </span>
            {!!availableForImport && (
              <span className='ml-2'>
                <i className='fa fa-plus mr-1'></i>
                Available for import
              </span>
            )}
          </small>
        </main>
      </Tooltip>
    </li>
  )
}
